// Theme toggle. Persists preference in localStorage (the only thing Phase 0
// allows in client storage per reference/phase-1.md §7 — auth state, profile
// PII, and session lists are not persisted client-side).

import { useEffect, useState } from 'react';

const STORAGE_KEY = 'bumbum:theme';
type Theme = 'light' | 'dark';

function readStoredTheme(): Theme | null {
  if (typeof window === 'undefined') return null;
  const stored = window.localStorage.getItem(STORAGE_KEY);
  return stored === 'light' || stored === 'dark' ? stored : null;
}

function prefersDark(): boolean {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
}

function resolveInitialTheme(): Theme {
  return readStoredTheme() ?? (prefersDark() ? 'dark' : 'light');
}

export function applyInitialTheme() {
  if (typeof document === 'undefined') return;
  const theme = resolveInitialTheme();
  document.documentElement.classList.toggle('dark', theme === 'dark');
}

export function persistTheme(theme: Theme) {
  if (typeof document === 'undefined') return;
  document.documentElement.classList.toggle('dark', theme === 'dark');
  window.localStorage.setItem(STORAGE_KEY, theme);
}

export function ThemeToggle() {
  const [theme, setThemeState] = useState<Theme>('light');

  useEffect(() => {
    setThemeState(resolveInitialTheme());
  }, []);

  const next: Theme = theme === 'dark' ? 'light' : 'dark';
  return (
    <button
      type="button"
      aria-label={`Switch to ${next} theme`}
      className="rounded border border-gray-300 px-2 py-1 text-xs hover:bg-gray-100 dark:border-gray-700 dark:hover:bg-gray-800"
      onClick={() => {
        persistTheme(next);
        setThemeState(next);
      }}
    >
      {theme === 'dark' ? '☀︎' : '☾'}
    </button>
  );
}
